import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import './EventViewer.css';

const EventViewer = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        action: '',
        userId: '',
        page: 1,
        pageSize: 50
    });
    const [pagination, setPagination] = useState({});
    const [availableActions, setAvailableActions] = useState([]);

    useEffect(() => {
        loadAvailableActions();
        loadAnalytics();
    }, []);

    useEffect(() => {
        loadLogs();
    }, [filters]);

    const loadAnalytics = async () => {
        try {
            const response = await fetch('/api/auditlog/analytics', {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setAnalytics(data);
            }
        } catch (error) {
            console.error('Failed to load analytics:', error);
        }
    };

    const loadAvailableActions = async () => {
        try {
            const response = await fetch('/api/auditlog/actions', {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableActions(data);
            }
        } catch (error) {
            console.error('Failed to load actions:', error);
        }
    };

    const loadLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            if (filters.action) params.append('action', filters.action);
            if (filters.userId) params.append('userId', filters.userId);
            params.append('page', filters.page);
            params.append('pageSize', filters.pageSize);

            const response = await fetch(`/api/auditlog?${params}`, {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setLogs(data.logs);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error('Failed to load logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            ...(field !== 'page' ? { page: 1 } : {})
        }));
    };

    const formatDateTime = (dateString) => {
        // Server returns UTC dates without Z suffix — ensure correct UTC parsing
        if (dateString && !dateString.endsWith('Z') && !dateString.includes('+')) {
            return new Date(dateString + 'Z').toLocaleString();
        }
        return new Date(dateString).toLocaleString();
    };

    const getActionColor = (action) => {
        if (action.includes('FAILED') || action.includes('UNAUTHORIZED')) return 'danger';
        if (action.includes('DELETE') || action.includes('DEACTIVATED')) return 'warning';
        if (action.includes('CREATE')) return 'success';
        if (action.includes('UPDATE') || action.includes('QUOTED')) return 'info';
        return 'default';
    };

    const renderDetails = (log) => {
        let details = [];
        
        // For User entities, show the target user's name if available, otherwise show entity info
        if (log.entityType && log.entityId) {
            // If this is a login action, show the target username from entityName if available
            if (log.entityName) {
                details.push(log.entityName);
            } else if (log.entityType === 'User' && log.targetUserName) {
                details.push(log.targetUserName);
            } else if (log.entityType !== 'User') {
                // For non-User entities, show the type and ID
                details.push(`${log.entityType} #${log.entityId}`);
            }
        }
        
        if (log.ipAddress) {
            details.push(`IP: ${log.ipAddress}`);
        }
        
        return details.join(' • ');
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'RFQ': return '📄';
            case 'User': return '👤';
            case 'Quote': return '💰';
            case 'Login': return '🔐';
            default: return '📌';
        }
    };

    return (
        <div className="event-viewer">
            <div className="event-header">
                <h1>Analytics & Event Log</h1>
                <p>Insights and activity tracking</p>
            </div>

            {/* Analytics Section */}
            {analytics && (
                <div className="analytics-section">
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon">📄</div>
                            <div className="stat-content">
                                <div className="stat-value">{analytics.rfqsThisMonth || 0}</div>
                                <div className="stat-label">RFQs This Month</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">👥</div>
                            <div className="stat-content">
                                <div className="stat-value">{analytics.activeUsers || 0}</div>
                                <div className="stat-label">Active Users</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">🔧</div>
                            <div className="stat-content">
                                <div className="stat-value">{analytics.configurationsThisMonth || 0}</div>
                                <div className="stat-label">Configurations</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">💰</div>
                            <div className="stat-content">
                                <div className="stat-value">{analytics.quotesSent || 0}</div>
                                <div className="stat-label">Quotes Sent</div>
                            </div>
                        </div>
                    </div>

                    {/* Monthly Trend */}
                    {analytics.monthlyTrend && analytics.monthlyTrend.length > 0 && (
                        <div className="trend-section">
                            <h3>Monthly RFQ Trend</h3>
                            <div className="trend-chart">
                                {analytics.monthlyTrend.map((month, index) => (
                                    <div key={index} className="trend-bar-container">
                                        <div 
                                            className="trend-bar" 
                                            style={{ 
                                                height: `${Math.max(10, (month.count / Math.max(...analytics.monthlyTrend.map(m => m.count))) * 100)}%` 
                                            }}
                                        >
                                            <span className="trend-value">{month.count}</span>
                                        </div>
                                        <span className="trend-label">{month.month}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Top Products */}
                    {analytics.topProducts && analytics.topProducts.length > 0 && (
                        <div className="top-products-section">
                            <h3>Top Products</h3>
                            <div className="top-products-list">
                                {analytics.topProducts.map((product, index) => (
                                    <div key={index} className="top-product-item">
                                        <span className="product-rank">#{index + 1}</span>
                                        <span className="product-name">{product.name}</span>
                                        <span className="product-count">{product.count} configs</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Divider */}
            <div className="section-divider">
                <h2>Recent Activity</h2>
            </div>

            {/* Filters */}
            <div className="event-filters">
                <div className="filter-group">
                    <label>Start Date</label>
                    <input
                        type="datetime-local"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    />
                </div>
                <div className="filter-group">
                    <label>End Date</label>
                    <input
                        type="datetime-local"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    />
                </div>
                <div className="filter-group">
                    <label>Action</label>
                    <select
                        value={filters.action}
                        onChange={(e) => handleFilterChange('action', e.target.value)}
                    >
                        <option value="">All Actions</option>
                        {availableActions.map(action => (
                            <option key={action.value} value={action.value}>
                                {action.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Per Page</label>
                    <select
                        value={filters.pageSize}
                        onChange={(e) => handleFilterChange('pageSize', Number(e.target.value))}
                    >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={250}>250</option>
                    </select>
                </div>
                <button
                    className="btn-clear"
                    onClick={() => setFilters({
                        startDate: '',
                        endDate: '',
                        action: '',
                        userId: '',
                        page: 1,
                        pageSize: 50
                    })}
                >
                    Clear Filters
                </button>
            </div>

            {loading ? (
                <div className="loading">Loading events...</div>
            ) : (
                <>
                    <table className="event-table">
                        <thead>
                            <tr>
                                <th>Date/Time</th>
                                <th>User</th>
                                <th>Company</th>
                                <th>Action</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(log => (
                                <tr key={log.id}>
                                    <td>{formatDateTime(log.createdAt)}</td>
                                    <td>
                                        {log.userName}
                                        {log.userEmail && (
                                            <div className="email-sub">{log.userEmail}</div>
                                        )}
                                    </td>
                                    <td>{log.companyName || '-'}</td>
                                    <td>
                                        <span className={`action-badge ${getActionColor(log.action)}`}>
                                            {log.action}
                                        </span>
                                    </td>
                                    <td className="details">{renderDetails(log)}</td>
                                </tr>
                            ))}
                            {logs.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="no-data">No events found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {pagination.totalPages > 1 && (
                        <div className="pagination">
                            <button
                                disabled={filters.page === 1}
                                onClick={() => handleFilterChange('page', filters.page - 1)}
                            >
                                Previous
                            </button>
                            <span>Page {filters.page} of {pagination.totalPages}</span>
                            <button
                                disabled={filters.page === pagination.totalPages}
                                onClick={() => handleFilterChange('page', filters.page + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default EventViewer;